import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Button,
  Divider,
  IconButton,
  Box,
  Avatar,
} from "@mui/material";
import TextInput from "../../Components/InputComponents/TextInput";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";

import CommonButton from "../../Components/InputComponents/CommonButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../ToastComponent/ToastNotification";
import { axiosAPI, axiosupload } from "../AxiosComponents/Axios";

import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useStaffLayout } from "../Context/CandidateLayoutContext";

const Settings = ({ isAdmin }) => {
  const { triggerProfileAPI } = useStaffLayout();
  const [SS_CompanyName, setSS_CompanyName] = useState(null);
  const [SS_CompanyEmail, setSS_CompanyEmail] = useState(null);
  const [SS_CompanyContactNo, setSS_CompanyContactNo] = useState(null);
  const [SS_CompanyAddress, setSS_CompanyAddress] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [Deleted, setDeleted] = useState(false);

  const [uploadedCompanyLogo, setUCL] = useState("");

  const [previewUrl, setPreviewUrl] = useState("");

  const [btnDisabled, setIsBtnDisabled] = useState(true);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const MAX_SIZE_MB = 30;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2); // rounded to 2 decimal places

    if (file.size > MAX_SIZE_BYTES) {
      infoNotify(`File size should be less than ${MAX_SIZE_MB} MB. Uploaded file size is ${fileSizeMB} MB`);
      return;
    }

    setCompanyLogo(event.target.files[0]);
    setDeleted(false);

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const saveFn = async () => {
    // Basic validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const contactNumberRegex = /^[0-9]{10}$/; // Adjust as needed for your use case

    // Email validation
    if (SS_CompanyEmail?.trim()) {
      if (!emailRegex.test(SS_CompanyEmail.trim())) {
        infoNotify("Please enter a valid email address.");
        return;
      }
    }

    // Contact number validation
    if (SS_CompanyContactNo?.trim()) {
      if (!contactNumberRegex.test(SS_CompanyContactNo.trim())) {
        infoNotify("Please enter a valid Contact number.");
        return;
      }
    }

    const formData = new FormData();
    formData.append("SS_CompanyName", SS_CompanyName);
    formData.append("SS_CompanyEmail", SS_CompanyEmail);
    formData.append("SS_CompanyContactNo", SS_CompanyContactNo);
    formData.append("SS_CompanyAddress", SS_CompanyAddress);

    let useExisting = false;
    let logoToSend = null;

    if (Deleted) {
      useExisting = false;
    } else if (companyLogo && !uploadedCompanyLogo) {
      logoToSend = companyLogo;
      useExisting = false;
    } else if (companyLogo == null && uploadedCompanyLogo) {
      useExisting = true;
    } else if (companyLogo && uploadedCompanyLogo) {
      logoToSend = companyLogo;
      useExisting = false;
    } else {
      useExisting = false;
    }

    formData.append("Companylogo", logoToSend);
    formData.append("IS_UseExisting_Image", useExisting);

    setIsBtnDisabled(true);
    
    try {
      const res = await axiosupload.post(`/settings/add`, formData);
      if (res?.data?.success) {
        successNotify(res?.data?.data?.message);
        triggerProfileAPI()
      } else {
        errorNotify(res?.data?.data?.message);
      }
    } catch (err) {
      errorNotify("Something went wrong while saving settings.");
    }
  };

  const getData = async () => {
    await axiosAPI
      ?.get(`/settings/view`)
      .then((res) => {
        setSS_CompanyName(res?.data?.data?.SS_CompanyName);
        setSS_CompanyEmail(res?.data?.data?.SS_CompanyEmail);
        setSS_CompanyContactNo(res?.data?.data?.SS_CompanyContactNo);
        setSS_CompanyAddress(res?.data?.data?.SS_CompanyAddress);
        setPreviewUrl(
          res?.data?.data?.SS_Companylogo
            ? `${process.env.REACT_APP_BASE_URL}/${res?.data?.data?.SS_Companylogo}`
            : null
        );
        setUCL(res?.data?.data?.SS_Companylogo ? true : false);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const { privilegeData } = useStaffLayout();

  function hasSettingsEdit(privilegeData) {
    if (isAdmin) return true;
    if (privilegeData.length == 0) return true;
    return privilegeData.some((subModule) => {
      const isClient = subModule.subModuleName
        .toLowerCase()
        .includes("settings");
      if (!isClient) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "edit" && priv.assigned === true
      );
    });
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, []);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Main Heading ================================================================================================================================================================== */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        Settings
      </Typography>

      <Grid
        container
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "20px",
          alignItems: "flex-start",
        }}
        my={2}
      >
        {/* Company Logo */}
        <Grid item xs={12} md={4} display="flex" justifyContent="center">
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif", mb: 1 }}
            >
              Company Logo
            </Typography>

            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                width: "100%",
                maxWidth: 350,
                minWidth: 300,
              }}
            >
              {previewUrl ? (
                <>
                  <label >
                    <Avatar
                      src={previewUrl}
                      sx={{
                        width: "100%",
                        height: 250,
                        borderRadius: "8px",
                        border: "2px solid grey",
                        // cursor: "pointer",
                      }}
                      variant="rounded"
                    />
                  </label>

                  {hasSettingsEdit(privilegeData) && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setPreviewUrl(null);
                        setCompanyLogo(null);
                        setDeleted(true);
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        zIndex: 1,
                        "&:hover": {
                          backgroundColor: "#f8d7da",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ color: "#e74c3c", fontSize: 18 }} />
                    </IconButton>
                  )}
                </>
              ) : (
                <label htmlFor="picture-upload">
                  <Avatar
                    sx={{
                      width: "100%",
                      height: 250,
                      borderRadius: "8px",
                      border: "2px solid grey",
                      cursor: hasSettingsEdit(privilegeData)
                        ? "pointer"
                        : "default",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    variant="rounded"
                  >
                    <IconButton component="span">
                      <CameraAltIcon />
                    </IconButton>
                  </Avatar>
                </label>
              )}
            </Box>
            {hasSettingsEdit(privilegeData) && (
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="picture-upload"
                type="file"
                onChange={handleFileChange}
              />
            )}
          </Box>
        </Grid>

        {/* Company Details */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5.7} lg={3.9} mx={1}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Company Name
              </Typography>
              <TextInput
                label="Company Name"
                placeholder="Enter Company Name"
                name="SS_CompanyName"
                value={SS_CompanyName}
                onChange={(e) => setSS_CompanyName(e.target.value)}
                isDisabled={!hasSettingsEdit(privilegeData)}
              />
            </Grid>

            <Grid item xs={12} sm={5.7} lg={3.9} mx={1}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Company Email
              </Typography>
              <TextInput
                label="Company Email"
                placeholder="Enter Company Email"
                name="SS_CompanyEmail"
                value={SS_CompanyEmail}
                onChange={(e) => setSS_CompanyEmail(e.target.value)}
                isDisabled={!hasSettingsEdit(privilegeData)}
              />
            </Grid>

            <Grid item xs={12} sm={5.7} lg={3.9} mx={1}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Company Contact Number
              </Typography>
              <TextInput
                placeholder="Enter Company Contact Number"
                type="text"
                name="SS_CompanyContactNo"
                value={SS_CompanyContactNo}
                onChange={(e) => setSS_CompanyContactNo(e.target.value)}
                // allowOnlyNumbers={true}
                maxLength={15}
                isDisabled={!hasSettingsEdit(privilegeData)}
              />
            </Grid>

            <Grid item xs={12} mx={1}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Company Address
              </Typography>
              <TextAreaInput
                label="Company Address"
                placeholder="Enter Company Address"
                name="SS_CompanyAddress"
                value={SS_CompanyAddress}
                onChange={(e) => setSS_CompanyAddress(e.target.value)}
                isDisabled={!hasSettingsEdit(privilegeData)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Buttons */}
      <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        {hasSettingsEdit(privilegeData) && (
          <Grid item>
            <CommonButton
              variant="contained"
              isDisabled={btnDisabled}
              customColor="#007BFF"
              onClick={saveFn}
            >
              Save
            </CommonButton>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default Settings;
